import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  DollarSign, TrendingUp, Landmark, Calendar, ArrowUpRight,
  Loader2, BarChart3, Layers, Percent
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt$ = (v) => {
  if (v == null || v === 0) return "$0";
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmt$k = (v) => {
  if (v == null || v === 0) return "$0";
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export default function ServicingRevenueDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/servicing/admin/servicing-revenue`)
      .then(r => setData(r.data))
      .catch(err => console.error("Servicing revenue fetch error:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="servicing-revenue-loading">
        <Loader2 className="h-7 w-7 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-sm text-[#888]" data-testid="servicing-revenue-empty">
        Unable to load servicing revenue data.
      </div>
    );
  }

  const s = data.summary || {};
  const monthly = data.monthly_data || [];
  const deals = data.deal_breakdown || [];
  const maxMonthly = Math.max(...monthly.map(m => m.revenue), 1);

  return (
    <div className="space-y-5" data-testid="servicing-revenue-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-2.5">
            <DollarSign className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <h2 className="text-base font-bold text-[#0A0A0A]" data-testid="servicing-revenue-title">Servicing Revenue</h2>
            <p className="text-[11px] text-[#888]">Monthly 0.25% (25 bps) servicing fee on funded portfolio</p>
          </div>
        </div>
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5">
          <span className="text-xs font-bold text-emerald-700" data-testid="servicing-revenue-rate">25 bps annualized</span>
        </div>
      </div>

      {/* Revenue KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="servicing-revenue-kpis">
        {[
          {
            label: "Total Servicing Revenue",
            value: fmt$(s.total_servicing_revenue),
            sub: `${s.total_fee_count} fees collected`,
            icon: DollarSign, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200"
          },
          {
            label: "Projected Annual Revenue",
            value: fmt$k(s.projected_annual_revenue),
            sub: `${fmt$(s.projected_monthly_revenue)}/month`,
            icon: TrendingUp, color: "text-blue-600", bg: "bg-blue-50 border-blue-200"
          },
          {
            label: "Funded Portfolio",
            value: fmt$k(s.funded_portfolio_value),
            sub: `${s.funded_deal_count} active deals`,
            icon: Landmark, color: "text-[#8B7340]", bg: "bg-amber-50 border-amber-200"
          },
          {
            label: "Avg Fee / Payment",
            value: fmt$(s.avg_fee_per_payment),
            sub: "per payment recorded",
            icon: Percent, color: "text-indigo-600", bg: "bg-indigo-50 border-indigo-200"
          },
        ].map((kpi, i) => (
          <motion.div key={kpi.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className="rounded-xl border border-[#e8e0d0] bg-white p-4"
            data-testid={`revenue-kpi-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`rounded-lg ${kpi.bg} p-1.5`}>
                <kpi.icon className={`h-3.5 w-3.5 ${kpi.color}`} />
              </div>
              <span className="text-[9px] uppercase tracking-wider text-[#999] font-semibold">{kpi.label}</span>
            </div>
            <div className={`text-xl font-black ${kpi.color}`}>{kpi.value}</div>
            <div className="text-[10px] text-[#888] mt-0.5">{kpi.sub}</div>
          </motion.div>
        ))}
      </div>

      {/* Monthly Revenue Chart */}
      {monthly.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="rounded-xl border border-[#e8e0d0] bg-white p-5" data-testid="monthly-revenue-chart">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-emerald-600" />
              <h3 className="text-sm font-bold text-[#0A0A0A]">Monthly Servicing Revenue</h3>
            </div>
            <span className="text-[10px] text-[#888]">{monthly.length} month{monthly.length !== 1 ? 's' : ''} of data</span>
          </div>

          <div className="flex items-end gap-2 h-40">
            {monthly.map((m, i) => {
              const height = Math.max(8, (m.revenue / maxMonthly) * 100);
              const monthLabel = new Date(m.month + "-01").toLocaleDateString("en-US", { month: "short", year: "2-digit" });
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1" data-testid={`revenue-bar-${i}`}>
                  <span className="text-[9px] font-bold text-emerald-700">{fmt$(m.revenue)}</span>
                  <motion.div
                    initial={{ height: 0 }} animate={{ height: `${height}%` }}
                    transition={{ delay: i * 0.1, duration: 0.4, ease: "easeOut" }}
                    className="w-full rounded-t-lg bg-gradient-to-t from-emerald-600 to-emerald-400 relative group cursor-default min-h-[8px]"
                  >
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#0A0A0A] text-white text-[9px] px-2 py-1 rounded whitespace-nowrap z-10">
                      {m.payments} payments
                    </div>
                  </motion.div>
                  <span className="text-[9px] text-[#888]">{monthLabel}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Deal Breakdown */}
      {deals.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid="deal-revenue-breakdown">
          <div className="px-5 py-3 border-b border-[#e8e0d0] flex items-center gap-2">
            <Layers className="h-4 w-4 text-[#8B7340]" />
            <h3 className="text-sm font-bold text-[#0A0A0A]">Revenue by Deal</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#faf7f2] border-b border-[#e8e0d0]">
                  <th className="px-5 py-2.5 text-left text-[10px] uppercase tracking-wider text-[#888] font-semibold">Deal</th>
                  <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-wider text-[#888] font-semibold">Borrower</th>
                  <th className="px-4 py-2.5 text-right text-[10px] uppercase tracking-wider text-[#888] font-semibold">Loan Amount</th>
                  <th className="px-4 py-2.5 text-center text-[10px] uppercase tracking-wider text-[#888] font-semibold">Payments</th>
                  <th className="px-4 py-2.5 text-right text-[10px] uppercase tracking-wider text-[#888] font-semibold">Servicing Fees</th>
                  <th className="px-4 py-2.5 text-right text-[10px] uppercase tracking-wider text-[#888] font-semibold">Annual Proj.</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((deal, i) => (
                  <tr key={deal.deal_id} className="border-b border-[#f5f0e8] hover:bg-[#fdfcfa] transition-colors" data-testid={`deal-revenue-row-${i}`}>
                    <td className="px-5 py-3">
                      <span className="font-bold text-[#0A0A0A]">{deal.deal_number}</span>
                    </td>
                    <td className="px-4 py-3 text-[#333]">{deal.borrower_name || "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[#0A0A0A]">{fmt$k(deal.loan_amount)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200">
                        {deal.payment_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-700">{fmt$(deal.total_servicing_fees)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="flex items-center justify-end gap-1 text-xs text-blue-600 font-semibold">
                        <ArrowUpRight className="h-3 w-3" />
                        {fmt$(deal.loan_amount * 0.0025)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Empty state */}
      {monthly.length === 0 && deals.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-[#e8e0d0] bg-[#faf7f2] p-10 text-center" data-testid="no-revenue-data">
          <DollarSign className="h-10 w-10 text-[#ccc] mx-auto mb-3" />
          <h3 className="text-sm font-bold text-[#666] mb-1">No Servicing Revenue Yet</h3>
          <p className="text-xs text-[#999] max-w-sm mx-auto">
            Servicing fees are automatically generated when payments are recorded on funded deals.
            Each payment creates a 0.25% annualized (0.0208% monthly) fee.
          </p>
        </div>
      )}
    </div>
  );
}
